import { System } from './System'
import { createNode } from '../extras/createNode'
import * as THREE from '../extras/three'
import { validElevatorPassenger, elevatorPassengerWorld } from '../extras/elevatorPassenger'
import { ElevatorReservation } from '../extras/elevatorReservation'
import { Layers } from '../extras/Layers'
import { ElevatorXR } from '../extras/elevatorXR'
import { travelToRoom } from '../extras/doorPortals'
import { elevatorTrip, elevatorSample, elevatorNear, elevatorLanding, elevatorRecallWait } from '../extras/elevatorMotion'

const key = id => id.toLowerCase().replace(/[^a-z0-9]/g, '')
const platformKeys = new Set(['ascensore2collider', 'ascensore2collider001collider', 'receptionledelevatorcollider', 'receptionledelevatorcollider001collider'])

// One shared trip, mirrored by both physics worlds. No GLB or DB writes.
export class Elevator extends System {
  constructor(world) {
    super(world)
    this.trip = elevatorTrip(0, 0, 0)
    this.origin = new THREE.Vector3()
    this.target = new THREE.Vector3()
    this.orientation = new THREE.Quaternion()
    this.status = { available: false }
    this.lastUI = 0
    this.reservation = new ElevatorReservation()
  }

  prepare(root) {
    this.uiMarkers = new Map()
    const duplicates = new Set()
    root.traverse(node => {
      if (node.name !== 'group' || !/^elevator_(?:call|panel)_[0-3]$/.test(node.id || '')) return
      if (this.uiMarkers.has(node.id)) duplicates.add(node.id)
      else this.uiMarkers.set(node.id, node)
    })
    for (const id of duplicates) this.uiMarkers.delete(id)
    const parts = []
    root.traverse(node => { if (platformKeys.has(key(node.id || ''))) parts.push(node) })
    const platform = parts.find(node => node.name === 'mesh' && key(node.id) === 'ascensore2collider')
    const collider = parts.find(node => node.name === 'collider' && key(node.id) === 'ascensore2collider001collider')
    if (!platform || !collider) return
    // Legacy Reception assets are direct scene children; fail closed on a changed hierarchy.
    if (platform.parent !== root || collider.parent?.parent !== root || parts.some(node => node.parent !== root && node.parent !== collider.parent)) return
    this.origin.copy(platform.position)
    this.body = createNode('rigidbody', { type: 'kinematic', tag: 'reception-elevator', position: this.origin.toArray() })
    for (const part of parts) {
      part.position.sub(this.origin)
      if (part.name === 'mesh') part.linked = false
      this.body.add(part)
    }
    root.add(this.body)
  }

  mounted() {
    if (!this.body) return
    // A client joining midway starts at the current shared pose, not piano 0.
    const sample = elevatorSample(this.trip, this.now())
    this.target.copy(this.origin).y += sample.offset
    this.body.setPosition(this.target)
    const pose = this.body.actor.getGlobalPose()
    this.body.actorHandle.snap(pose)
    if (this.world.network.isServer) this.world.network.send('elevatorState', this.serialize())
  }

  now() { return this.world.network.getTime() || 0 }

  serialize() { return this.body ? { ...this.trip, owner: this.reservation.owner, until: this.reservation.until } : null }

  receive(trip, initial = false) {
    if (!trip) return
    const validated = elevatorTrip(trip.from, trip.to, trip.startedAt)
    if (validated) {
      this.trip = validated
      this.reservation.owner = typeof trip.owner === 'string' ? trip.owner : null
      this.reservation.until = Number.isFinite(trip.until) ? trip.until : 0
      if (initial && this.body?.actor) this.mounted()
    }
  }

  request(socket, floor, call = false) {
    if (!this.body || !Number.isInteger(floor) || floor < 0 || floor > 3) return
    if (call) {
      // The server derives the destination from the caller's landing, never a client floor.
      floor = this.landing(this.passengerPosition(socket.player))
      if (floor === null) return
    } else if (!this.supports(this.passengerPosition(socket.player))) return
    const now = this.now()
    this.refreshReservation(now)
    if (call && elevatorRecallWait(this.trip, now) > 0) return
    if (!this.reservation.permits(socket.id, now)) return
    if (elevatorSample(this.trip, now).moving || this.trip.to === floor) return
    this.trip = elevatorTrip(this.trip.to, floor, now)
    this.world.network.send('elevatorState', this.serialize())
  }

  normalizePassenger(data) {
    if (data.t || data.ef?.anchorId) data.ep = null
    if (!Object.hasOwn(data, 'ep')) return
    if (!this.body?.actor || !validElevatorPassenger(data.ep)) {
      data.ep = null
      return
    }
    const position = elevatorPassengerWorld(data.ep, this.body.position)
    if (!this.supports(position)) {
      data.ep = null
      return
    }
    // Server-owned platform height, not the delayed absolute height in the packet.
    data.p = position
  }

  passengerPosition(player) {
    return this.body && validElevatorPassenger(player?.data.elevatorPosition)
      ? elevatorPassengerWorld(player.data.elevatorPosition, this.body.position)
      : player?.data.position
  }

  supports(position) {
    if (!this.body?.actor || !elevatorNear(position, this.origin)) return false
    const feet = new THREE.Vector3().fromArray(position)
    feet.y += 0.25
    const support = this.world.physics.raycast(feet, new THREE.Vector3(0, -1, 0), 0.7, Layers.environment.group)
    return support?.handle?.actor === this.body.actor
  }

  landing(position) {
    if (!this.body?.actor) return null
    const floor = elevatorLanding(position, this.origin)
    if (floor === null) return null
    const feet = new THREE.Vector3().fromArray(position)
    feet.y += 0.25
    const support = this.world.physics.raycast(feet, new THREE.Vector3(0, -1, 0), 0.7, Layers.environment.group)
    return support && support.handle?.actor !== this.body.actor ? floor : null
  }

  call() {
    if (this.status.callFloor === null || this.status.callFloor === undefined || this.status.occupied || this.status.moving || this.status.recallWait > 0 || this.world.network.ws?.readyState !== 1) return
    this.world.network.send('elevatorCall', null)
  }

  refreshReservation(now) {
    const ids = []
    for (const socket of this.world.network.sockets.values()) {
      if (this.supports(this.passengerPosition(socket.player))) ids.push(socket.id)
    }
    if (this.reservation.update(ids, now, elevatorSample(this.trip, now).moving)) {
      this.world.network.send('elevatorState', this.serialize())
    }
  }

  goToCourseRoom() {
    if (!this.status.aboard || this.status.moving || this.world.network.isServer) return
    return travelToRoom(this.world, 'courseroom')
  }

  select(floor) {
    if (!this.reservation.permits(this.world.network.id, this.now())) return
    if (!this.body || elevatorSample(this.trip, this.now()).moving || this.world.network.ws?.readyState !== 1) return
    this.world.network.send('elevatorRequest', floor)
  }

  fixedUpdate() {
    if (!this.body?.actor) return
    const sample = elevatorSample(this.trip, this.now())
    this.target.copy(this.origin).y += sample.offset
    this.body.setKinematicTarget(this.target, this.orientation)
  }

  update(delta) {
    if (!this.world.network.isServer && this.body?.actor) {
      if (this.world.xr.session && !this.xrControls) this.xrControls = new ElevatorXR(this.world, this)
      this.xrControls?.update()
    }
    this.lastUI += delta
    if (this.lastUI < 0.1) return
    this.lastUI = 0
    if (this.world.network.isServer) {
      if (this.body?.actor) this.refreshReservation(this.now())
      return
    }
    const player = this.world.entities.player
    // The shared PhysX body includes the platform and its LED disc. Show the
    // controller only when that body actually supports the local player.
    const aboard = !!this.body?.actor && !!player?.grounded && player.platform.actor === this.body.actor
    const sample = elevatorSample(this.trip, this.now())
    const landing = this.body && player?.grounded && !aboard ? elevatorLanding(player.base.position.toArray(), this.origin) : null
    const callFloor = landing !== this.trip.to || sample.moving ? landing : null
    const status = { available: aboard || callFloor !== null, aboard, callFloor, recallWait: Math.ceil(elevatorRecallWait(this.trip, this.now())), moving: sample.moving, floor: this.trip.to, from: this.trip.from, occupied: !this.reservation.permits(this.world.network.id, this.now()) }
    if (JSON.stringify(status) !== JSON.stringify(this.status)) {
      this.status = status
      this.world.emit('elevator', status)
    }
  }

  destroy() {
    this.xrControls?.destroy()
    this.xrControls = null
    this.uiMarkers?.clear()
    this.body?.parent?.remove(this.body)
    this.body = null
  }
}
